import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Stack,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Keyboard as KeyboardIcon,
  LocalPharmacy as PharmacyIcon,
  PointOfSale as PosIcon,
  Navigation as NavIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

interface ShortcutItem {
  keys: string[];
  action: string;
  description: string;
  category: "pharma" | "pos" | "nav" | "print";
  badge?: string;
}

const ALL_SHORTCUTS: ShortcutItem[] = [
  // Pharmacy & Marg Billing
  {
    keys: ["F2"],
    action: "Select Customer / Patient",
    description: "Focuses customer mobile number or patient selection dialog",
    category: "pharma",
    badge: "Marg POS",
  },
  {
    keys: ["F3"],
    action: "Search Medicine / Product",
    description: "Opens medicine catalog search to quickly add drugs & strips to bill",
    category: "pharma",
    badge: "Marg POS",
  },
  {
    keys: ["F4"],
    action: "Return / Credit Note",
    description: "Look up prior bill number and return sold items with inventory restocking",
    category: "pharma",
    badge: "Return",
  },
  {
    keys: ["F6"],
    action: "Fast Cash Payment",
    description: "Immediately selects Cash mode and processes payment",
    category: "pharma",
  },
  {
    keys: ["F7"],
    action: "UPI / QR Code Payment",
    description: "Selects UPI payment and generates QR code for customer scanning",
    category: "pharma",
  },
  {
    keys: ["F8"],
    action: "Card / POS Swipe Payment",
    description: "Selects Debit/Credit Card payment method",
    category: "pharma",
  },
  {
    keys: ["F9"],
    action: "Store Credit / Khata",
    description: "Registers bill to customer's ledger/credit balance",
    category: "pharma",
  },
  {
    keys: ["F10"],
    action: "Print Bill / Invoice",
    description: "Instant print dialog (select A5 half sheet, A4 full, or 80mm thermal)",
    category: "pharma",
    badge: "Printing",
  },
  {
    keys: ["F12"],
    action: "Hold Current Bill",
    description: "Holds active bill to serve another customer and resume later",
    category: "pharma",
  },
  {
    keys: ["Tab", "Enter"],
    action: "Next Cell / Add New Row",
    description: "Seamlessly moves between Medicine Name, Qty, Batch, Expiry, and Discount",
    category: "pharma",
  },
  {
    keys: ["Delete", "Backspace"],
    action: "Remove Medicine Line",
    description: "Deletes selected row from bill when quantity is empty or on delete key",
    category: "pharma",
  },
  {
    keys: ["Esc"],
    action: "Close Dialog / Cancel Selection",
    description: "Dismisses active modal, popup or clears input focus",
    category: "pharma",
  },

  // POS & Retail
  {
    keys: ["Ctrl", "/"],
    action: "Toggle POS Shortcuts Bar",
    description: "Shows or hides the bottom keyboard shortcuts cheat sheet",
    category: "pos",
  },
  {
    keys: ["+"],
    action: "Increase Quantity",
    description: "Increments quantity of currently active cart item",
    category: "pos",
  },
  {
    keys: ["-"],
    action: "Decrease Quantity",
    description: "Decrements quantity of currently active cart item",
    category: "pos",
  },
  {
    keys: ["Alt", "N"],
    action: "New Bill / Reset Cart",
    description: "Clears current billing grid to start a fresh invoice",
    category: "pos",
  },
  {
    keys: ["Alt", "D"],
    action: "Apply Bill Discount",
    description: "Opens discount popup to enter percentage or flat discount",
    category: "pos",
  },

  // Print
  {
    keys: ["Ctrl", "P"],
    action: "Print Active Screen / Invoice",
    description: "Triggers system or POS thermal/laser invoice printer",
    category: "print",
  },

  // Navigation
  {
    keys: ["Ctrl", "K"],
    action: "Global Search",
    description: "Quick navigation to any report, menu, product, or customer",
    category: "nav",
  },
  {
    keys: ["Alt", "H"],
    action: "Go to Dashboard / Home",
    description: "Navigates directly to main overview dashboard",
    category: "nav",
  },
];

export default function ShortcutsPage() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");

  const filtered = ALL_SHORTCUTS.filter((s) => {
    const matchesCategory =
      activeTab === "all" ? true : s.category === activeTab;
    const matchesSearch =
      !search.trim() ||
      s.action.toLowerCase().includes(search.toLowerCase()) ||
      s.description.toLowerCase().includes(search.toLowerCase()) ||
      s.keys.some((k) => k.toLowerCase().includes(search.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <KeyboardIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h5" fontWeight={700}>
            Keyboard Shortcuts
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Speed up pharmacy dispensing, Marg billing, POS checkouts, and navigation without touching the mouse.
        </Typography>
      </Box>

      {/* Quick highlight cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "primary.50", border: "1px solid", borderColor: "primary.200" }}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PharmacyIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                  Pharma Dispensing
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={800}>
                F2 · F3 · F4
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Patient lookup, Medicine search &amp; Return
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "success.50", border: "1px solid", borderColor: "success.200" }}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PosIcon color="success" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="success.main">
                  Instant Payment
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={800}>
                F6 · F7 · F8 · F9
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cash, UPI QR, Card &amp; Khata Udhaar
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200" }}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PrintIcon color="warning" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="warning.main">
                  Print Invoice
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={800}>
                F10 · Ctrl+P
              </Typography>
              <Typography variant="caption" color="text.secondary">
                A5 half sheet, A4 full, or 80mm thermal
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "info.50", border: "1px solid", borderColor: "info.200" }}>
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <NavIcon color="info" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} color="info.main">
                  Grid Navigation
                </Typography>
              </Stack>
              <Typography variant="h6" fontWeight={800}>
                Tab · Enter · Esc
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Next column, add line, close modal
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search & Tabs */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search shortcut (e.g. F4, Return, Cash, Print)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 340 } }}
          />

          <Tabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ flex: 1 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`All (${ALL_SHORTCUTS.length})`} value="all" />
            <Tab label="Pharmacy / Marg (F2-F10)" value="pharma" />
            <Tab label="POS & Checkouts" value="pos" />
            <Tab label="Printing" value="print" />
            <Tab label="Navigation" value="nav" />
          </Tabs>
        </Box>
      </Card>

      {/* Shortcuts Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead sx={{ bgcolor: "grey.100" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: 220 }}>Keys</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 260 }}>Action</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700, width: 140 }}>Category</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((item, idx) => (
              <TableRow key={idx} hover>
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {item.keys.map((k, kIdx) => (
                      <React.Fragment key={kIdx}>
                        {kIdx > 0 && (
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            +
                          </Typography>
                        )}
                        <Box
                          component="kbd"
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            bgcolor: "grey.900",
                            color: "#fff",
                            borderRadius: 1,
                            fontSize: "0.8125rem",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            boxShadow: "0 2px 0 rgba(0,0,0,0.3)",
                            fontFamily: "monospace",
                            display: "inline-block",
                          }}
                        >
                          {k}
                        </Box>
                      </React.Fragment>
                    ))}
                    {item.badge && (
                      <Chip
                        label={item.badge}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "0.6875rem", height: 20 }}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {item.action}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      item.category === "pharma"
                        ? "Pharmacy"
                        : item.category === "pos"
                        ? "POS"
                        : item.category === "print"
                        ? "Print"
                        : "Navigation"
                    }
                    size="small"
                    color={
                      item.category === "pharma"
                        ? "primary"
                        : item.category === "print"
                        ? "warning"
                        : "default"
                    }
                    variant="filled"
                    sx={{ textTransform: "capitalize", fontSize: "0.75rem" }}
                  />
                </TableCell>
              </TableRow>
            ))}

            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No shortcuts matched your search &quot;{search}&quot;.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Alert severity="info" variant="outlined">
          <strong>Tip:</strong> Press <code>F4</code> at any time while billing to initiate a return or look up a prior customer invoice.
        </Alert>
      </Box>
    </Box>
  );
}
